import { useEffect, useRef, useState } from "react";
import { animate, useInView } from "framer-motion";
import { GraduationCap, Award, MapPin, HeartPulse } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { LeafMotif } from "./NavyogaLogo";

const CREDENTIALS = [
  "M.D. in Acupuncture — Open International University (2025)",
  "M.Sc. in Yoga — S-VYASA, Bengaluru",
  "Master Diploma in Acupuncture Therapy — ATAMA",
  "Master Diploma in Cupping / Hijama Therapy",
  "Diploma in Colour Therapy",
  "Yoga Instructor Course (112 Credits) — S-VYASA",
];

const SPECIALIZATIONS = [
  "Cancer Rehab Yoga — The Yoga Institute",
  "Sujok Therapy",
  "Ayurveda",
  "Ashtanga & Yin Yoga",
];

const Counter = ({ to, suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 1.8,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to]);
  return (
    <span ref={ref} data-testid={`counter-${to}`}>
      {val}
      {suffix}
    </span>
  );
};

const STATS = [
  { icon: Award, value: <Counter to={7} suffix="+" />, label: "Years of Experience" },
  { icon: HeartPulse, value: <Counter to={8} suffix="+" />, label: "Integrative Disciplines" },
  { icon: GraduationCap, value: <Counter to={6} suffix="+" />, label: "Degrees & Diplomas" },
  { icon: MapPin, value: "A&N", label: "Islands Served, from Port Blair" },
];

export const About = () => (
  <section id="about" data-testid="about-navyoga-section" className="relative overflow-hidden bg-white py-20 lg:py-32">
    <LeafMotif className="absolute -left-6 top-24 h-32 w-32 text-[#2E8B57]/5" />
    <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
      <div className="grid gap-14 lg:grid-cols-2 lg:gap-20">
        <div>
          <SectionHeading
            testId="about-heading"
            overline="About NAVYOGA"
            title="A visionary wellness center, rooted in the islands"
            description="NAVYOGA was founded with a simple belief — that the ancient sciences of yoga and natural healing belong to everyone. From sunrise group batches to quiet one-on-one therapy rooms in Port Blair, the center blends classical Indian wisdom with modern, evidence-aware practice to serve the Andaman community."
          />
          <div className="mt-10 grid grid-cols-2 gap-5">
            {STATS.map((stat, i) => (
              <Reveal key={stat.label} delay={0.08 * i}>
                <div
                  data-testid={`about-stat-${i}`}
                  className="rounded-2xl border border-emerald-900/10 bg-[#F8FFF8] p-6 transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-[0_18px_40px_-18px_rgba(46,139,87,0.35)]"
                >
                  <stat.icon className="h-5 w-5 text-[#2E8B57]" />
                  <p className="font-heading mt-3 text-3xl font-semibold text-[#1E3A8A] sm:text-4xl">{stat.value}</p>
                  <p className="mt-1 text-xs font-medium uppercase tracking-wider text-slate-500">{stat.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.15}>
          <div
            data-testid="founder-card"
            className="relative rounded-3xl border border-emerald-900/10 bg-gradient-to-b from-white to-[#F0FDF4] p-8 shadow-[0_30px_70px_-30px_rgba(30,58,138,0.25)] sm:p-10 lg:sticky lg:top-28"
          >
            <div className="glow-emerald absolute -right-10 -top-10 h-40 w-40 rounded-full" aria-hidden="true" />
            <div className="relative">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-[#1E3A8A] text-white shadow-lg">
                <span className="font-heading text-2xl font-semibold">RA</span>
              </div>
              <p className="mt-6 text-xs font-bold uppercase tracking-[0.3em] text-[#2E8B57]">Founder & Lead Therapist</p>
              <h3 className="font-heading mt-2 text-2xl font-semibold text-slate-900 sm:text-3xl">Dr. Rajni Ahuja</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600 sm:text-base">
                7+ years of hands-on experience guiding island communities toward healthier, calmer lives through yoga
                and integrative therapies.
              </p>

              <div className="mt-7">
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-slate-400">Qualifications</p>
                <ul className="mt-4 space-y-3">
                  {CREDENTIALS.map((c) => (
                    <li key={c} className="flex items-start gap-3 text-sm text-slate-700">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#2E8B57]" />
                      {c}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-7">
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-slate-400">Specializations</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {SPECIALIZATIONS.map((s) => (
                    <span
                      key={s}
                      className="rounded-full border border-[#1E3A8A]/15 bg-white px-4 py-1.5 text-xs font-semibold text-[#1E3A8A]"
                    >
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
